import { type Ref, computed, ref, nextTick } from 'vue';
import type {
  Stripe,
  StripeElements
} from '@stripe/stripe-js';
import type { Element as ElementType, PageOptions } from '@zotlo-shared/types/model';
import { RegisterType } from '@zotlo-shared/types/model';
import { PaymentProvider, type PaymentForm, type PaymentPageData } from '@zotlo-shared/elements/common/payment/types';
import { getTotalPayableAmount } from '@zotlo-shared/elements/common/payment/utils';
import { PackagePaymentType } from '@zotlo-shared/elements/common/payment/types';
import { globalEmit } from '@zotlo-shared/utils/helpers';
import { useLabelOptions } from '@zotlo-shared/composable/useLabelOptions';
import { $wait } from '@zotlo-shared/utils/wait';

export type StripeForm = { stripe: Stripe, elements: StripeElements };

export function usePaymentElement({
  element,
  pageOptions,
  pageData
}: {
  element: Ref<ElementType<'payment' | 'payment-with-tabs'>>;
  pageOptions: Ref<PageOptions>;
  pageData: Ref<PaymentPageData>;
}) {
  const stripeForm = ref<StripeForm | null>(null);
  const paymentFormElement = ref<Record<string, any> | null>(null);
  const form: Ref<PaymentForm> = ref({
    email: pageData.value.bypassRegister && pageData.value.registerType !== RegisterType.Other ? pageData.value.subscriberId : '',
    cardNumber: '',
    cardHolder: '',
    cardExpirationDate: '',
    cardCVV: ''
  });
  const renderComponent = ref(true);

  const selectedPackageInfo = computed(() => pageData.value?.selectedPackage);
  const hasSelectedPackage = computed(() => !!selectedPackageInfo.value?.id);
  const isSubscriptionPackage = computed(() => selectedPackageInfo.value?.packageType === PackagePaymentType.SUBSCRIPTION);
  const isPriceDetailsCardVisible = computed(() => hasSelectedPackage.value && hasPaymentMethod.value && isSubscriptionPackage.value);
  const isSubmitLoading = computed(() => pageData?.value?.isSubmitLoading || false);
  const isSubmitDisabled = computed(() => pageData.value?.isSubmitDisabled || false);
  const isTrialUsed = computed(() => pageData.value?.isTrialUsed || false);
  const totalPrice = computed(() => {
    return getTotalPayableAmount(selectedPackageInfo.value, { isTrialUsed: isTrialUsed.value }) || '0.00 USD';
  });
  const subElements = computed(() => element.value.elements || []);
  const submitButtonElement = computed(() => subElements.value.find(el =>
    ['conditional-button', 'button'].includes(el.type)) as ElementType<'button' | 'conditional-button'>
  );
  const isLoadingPackage = computed(() => $wait.is('getPackages'));
  const hasPaymentMethod = computed(() => {
    const status = pageData.value?.providerStatus || {};
    return !!Object.values(status).filter(Boolean).length;
  })

  const { labelStyle, consentStyle, isLabelVisible } = useLabelOptions({ element, pageOptions });

  const isOtherPaymentMethodsActive = computed(() => {
    // Check active methods other than Credit Card
    return !!Object.keys(pageData?.value.providerStatus || {}).filter(
      (provider) => (
        ![PaymentProvider.CREDIT_CARD, PaymentProvider.STRIPE].includes(provider as PaymentProvider) &&
        getProviderStatus(provider as PaymentProvider)
      )
    ).length;
  });

  const providerByOrder = computed(() => {
    if (!element.value?.options?.order) {
      return Object.keys(pageData.value.providerStatus || {}).filter(
        (key) => !!pageData.value.providerStatus?.[key as PaymentProvider]
      );
    }

    return Object.entries(element.value.options.order || {})
      .sort(([, a], [, b]) => a - b)
      .filter(([key]) => !!pageData.value.providerStatus?.[key as PaymentProvider])
      .map(([key]) => key);
  })

  function getProviderStatus(provider: PaymentProvider) {
    return providerByOrder.value.includes(provider);
  }

  function prepareFormValues(form: PaymentForm) {
    const cardExpirationMonth = form.cardExpirationDate?.slice(0, 2);
    const cardExpirationYear = form.cardExpirationDate ? `20${form.cardExpirationDate?.slice(2, 4)}` : '';
    const payload = {
      ...form,
      cardExpirationMonth,
      cardExpirationYear
    };
    delete payload.cardExpirationDate;
    return payload;
  }

  function getFormValues() {
    const payload = prepareFormValues(form.value);
    return {
      form: payload
    };
  }

  function handleSubmit(provider: PaymentProvider = PaymentProvider.CREDIT_CARD) {
    globalEmit('paymentFormSubmitted', {
      ...getFormValues(),
      provider,
      stripeForm: stripeForm.value
    });
  }

  async function handleFormValidation(provider: PaymentProvider) {
    const isRegisterOnPayment = pageData.value.registerOnPayment;
    const thirdPartyIntegrations: PaymentProvider[] = [
      PaymentProvider.PAYPAL,
      PaymentProvider.APPLE_PAY,
      PaymentProvider.GOOGLE_PAY
    ];
    const skipValidation = thirdPartyIntegrations.includes(provider) && !isRegisterOnPayment;

    if (paymentFormElement.value && (!skipValidation || pageData.value.isPolicyRequired)) {
      pageData.value.paymentMethod = provider;
      await nextTick();
      const result = await paymentFormElement.value?.validate();
      if (!result?.valid) return false;
    }
    return true;
  }

  async function clickProvider(provider: PaymentProvider) {
    const valid = await handleFormValidation(provider);
    if (valid) handleSubmit(provider);
  }

  async function handleValidSubscriberIdEntered(subscriberId: string) {
    globalEmit('validSubscriberIdEntered', subscriberId);
  }

  async function forceRender() {
    renderComponent.value = false;
    await nextTick();
    renderComponent.value = true;
  }

  return {
    form,
    stripeForm,
    hasPaymentMethod,
    paymentFormElement,
    handleSubmit,
    handleValidSubscriberIdEntered,
    clickProvider,
    isSubmitLoading,
    isSubmitDisabled,
    isLoadingPackage,
    selectedPackageInfo,
    isPriceDetailsCardVisible,
    labelStyle,
    consentStyle,
    isLabelVisible,
    providerByOrder,
    getProviderStatus,
    isOtherPaymentMethodsActive,
    totalPrice,
    submitButtonElement,
    renderComponent,
    forceRender
  };
}
